import React, {useEffect} from "react";
import {AdminLayout} from "../../components/layout/AdminLayout";
import {useNavigate} from "react-router-dom";
import {AppDispatch, RootState} from "../../redux/store";
import {useDispatch, useSelector} from "react-redux";
import {getLencoTransactions, reset} from "../../redux/slice/transactionSlice";
import {logout} from "../../redux/slice/accountSlice";
import {Banknote} from "lucide-react";
import {DataTable} from "../../components/table/DataTable";
import {HeroHeader, Card, COLORS} from "../../designSystem";

function LencoTransactions() {
    const navigate = useNavigate();
    const dispatch: AppDispatch = useDispatch();
    const {lencoTransactions, pageInfo, error, loading} = useSelector((state: RootState) => state.transaction);
    const {token, isAuthenticated} = useSelector((state: RootState) => state.account);

    const [searchText, setSearchText] = React.useState("");
    const [groupBy, setGroupBy] = React.useState<string | undefined>();

    const columns = [
        {
            title: "#",
            key: "index",
            render: (value: any, item: any, index: any) => (pageInfo.pageNumber - 1) * 10 + index + 1,
        },
        {
            title: "Transaction Reference",
            dataIndex: "transactionReference",
            key: "transactionReference",
        },
        {
            title: "Amount",
            dataIndex: "amount",
            key: "amount",
            render: (text: string) => <span className="font-mono">{`₦${parseFloat(text).toLocaleString()}`}</span>,
        },
        {
            title: "Fee",
            dataIndex: "fee",
            key: "fee",
            render: (text: string) => <span className="font-mono">{`₦${parseFloat(text).toLocaleString()}`}</span>,
        },
        {
            title: "Narration",
            dataIndex: "narration",
            key: "narration",
        },
        {
            title: "Type",
            dataIndex: "type",
            key: "type",
        },
        {
            title: "Initiated At",
            dataIndex: "initiatedAt",
            key: "initiatedAt",
            render: (text: string) => {
                if (text === null) {
                    return ``
                }
                return new Date(text).toLocaleString()
            },
        },
        {
            title: "Completed At",
            dataIndex: "completedAt",
            key: "completedAt",
            render: (text: string) => {
                if (text === null) {
                    return ``
                }
                return new Date(text).toLocaleString()
            },
        },
        {
            title: "Account Name",
            dataIndex: "accountName",
            key: "accountName",
        },
        {
            title: "Account Number",
            dataIndex: "accountNumber",
            key: "accountNumber",
        },
        {
            title: "Bank Name",
            dataIndex: "bankName",
            key: "bankName",
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
        },
        {
            title: "Failed At",
            dataIndex: "failedAt",
            key: "failedAt",
            render: (text: string) => {
                if (text === null) {
                    return ``
                }
                return new Date(text).toLocaleString()
            },
        },
        {
            title: "Reason For Failure",
            dataIndex: "reasonForFailure",
            key: "reasonForFailure",
        },
        {
            title: "Client Reference",
            dataIndex: "clientReference",
            key: "clientReference",
        },
    ];

    useEffect(() => {
        if (token)
            dispatch(getLencoTransactions({
                token,
                searchParam: searchText,
                pageNumber: pageInfo?.pageNumber || 1
            })).then((res) => {
            }).catch((err) => console.log(err));

        return () => {
            // cleanup
        }
    }, [token, searchText]);

    useEffect(() => {
        if (!isAuthenticated) {
            navigate("/login");
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (error === 'Unauthorized') {
            dispatch(reset());
            dispatch(logout());
            navigate("/login");
        }
    }, [error]);

    const handlePageChange = (pageNumber: number) => {
        dispatch(getLencoTransactions({
            token,
            searchParam: searchText,
            pageNumber
        })).then((res) => {
        }).catch((err) => console.log(err));
    }

    return (
        <AdminLayout>
            <main className="h-full overflow-y-auto px-6 py-8 md:px-8" style={{background: COLORS.surface}}>
                <HeroHeader
                    icon={Banknote}
                    title="Lenco Transactions"
                    subtitle="Inspect settlement transactions processed through Lenco."
                />

                <Card className="mt-8 p-5">
                    <DataTable
                        columns={columns}
                        data={lencoTransactions}
                        pageInfo={pageInfo}
                        handlePageChange={handlePageChange}
                        loading={loading}
                        searchValue={searchText}
                        onSearchChange={setSearchText}
                        searchPlaceholder="Search by reference, account or name"
                        historyKey="lenco-transactions"
                        grouping={{
                            value: groupBy,
                            onChange: setGroupBy,
                            options: [
                                {key: "status", label: "Status", get: (row) => row.status},
                                {key: "type", label: "Type", get: (row) => row.type},
                                {key: "bankName", label: "Bank", get: (row) => row.bankName},
                                {
                                    key: "day",
                                    label: "Day initiated",
                                    get: (row) => row.initiatedAt ? new Date(row.initiatedAt).toLocaleDateString() : "",
                                },
                            ],
                        }}
                        exportFileName="lenco-transactions"
                        emptyText="No Lenco transactions match this search."
                    />
                </Card>
            </main>
        </AdminLayout>
    );
}

export default LencoTransactions;
